import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components';
import axios from 'axios';
import { 
  FiEdit, FiPlus, FiX, FiUser, FiDollarSign, 
  FiClock, FiTag, FiSearch, FiFilter, FiGrid, 
  FiList, FiChevronDown, FiInfo, FiBookOpen, FiTrash2
} from 'react-icons/fi';
import { useStateContext } from '../contexts/ContextProvider';

const RecordedCourse = () => {
  const { currentColor } = useStateContext();
  const navigate = useNavigate();
  const userRole = localStorage.getItem('role') || '';
  const isAdmin = userRole === 'admin';

  const [RecordedCourse, setRecordedCourse] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [courseToDelete, setCourseToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [notification, setNotification] = useState(null);
  
  // New state variables
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [filteredClasses, setFilteredClasses] = useState([]);
  const [studentCounts, setStudentCounts] = useState({});
  const [filterCategories, setFilterCategories] = useState(['all']);

  const isPreviousCourse = (course) => {
    return Boolean(course.access_end_date && new Date(course.access_end_date) < new Date());
  };

  const showToast = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3500);
  };

  const handleDeleteCourse = async () => {
    if (!courseToDelete) return;
    try {
      setDeleting(true);
      await axios.delete(`https://apissc.sainikschoolcadet.com/api/Contents/${courseToDelete.content_id}`, {
        headers: {
          'x-user-role': userRole,
          'x-user-email': localStorage.getItem('email') || '',
          'x-user-id': localStorage.getItem('user_id') || ''
        }
      });
      
      setRecordedCourse(prev => prev.filter(c => c.content_id !== courseToDelete.content_id));
      setFilteredClasses(prev => prev.filter(c => c.content_id !== courseToDelete.content_id));
      showToast('Previous course deleted successfully', 'success');
      setCourseToDelete(null);
    } catch (err) {
      console.error('Error deleting course:', err);
      showToast(err.response?.data?.message || 'Failed to delete course. Please try again.', 'error');
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    const fetchRecordedCourse = async () => {
      try {
        setLoading(true);
        const response = await axios.get('https://apissc.sainikschoolcadet.com/api/Contents/type/recorded_lecture');
        const classes = response.data.data;
        setRecordedCourse(classes);
        setFilteredClasses(classes);
        
        // Fetch student counts for each class
        const counts = {};
        for (const cls of classes) {
          try {
            const studentsResponse = await axios.get(`https://apissc.sainikschoolcadet.com/api/StudentContentAccess/content/${cls.content_id}/students`);
            counts[cls.content_id] = {
              total: studentsResponse.data.total_students_count,
              active: studentsResponse.data.active_students_count
            };
          } catch (error) {
            console.error(`Error fetching students for class ${cls.content_id}:`, error);
            counts[cls.content_id] = { total: 0, active: 0 };
          }
        }
        setStudentCounts(counts);
        setError(null);
      } catch (err) {
        console.error('Error fetching Recorded Coursees:', err);
        setError('Failed to load Recorded Coursees. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchRecordedCourse();
  }, []);

  // Extract filter categories from class titles
  useEffect(() => {
    if (RecordedCourse.length === 0) return;
    
    // Debug - log all titles to check content
    console.log("All class titles:", RecordedCourse.map(cls => cls.title));
    
    const keywordPatterns = [
      // Location patterns
      { regex: /gurugram/i, category: 'Gurugram' },
      { regex: /delhi/i, category: 'Delhi' },
      
      // Simple class patterns
      { regex: /6th/i, category: 'Class 6th' },
      { regex: /9th/i, category: 'Class 9th' },
      
      // SSQ patterns
      { regex: /ssq\s*(\d+)/i, category: null }, // Will extract dynamically
      
      // Mode patterns
      { regex: /online/i, category: 'Online' },
      { regex: /offline/i, category: 'Offline' }
    ];
    
    const categories = new Set(['all']);
    
    RecordedCourse.forEach(cls => {
      if (!cls.title) return; // Skip if title is missing
      
      // Explicit check for Class 6th
      if (/6th/i.test(cls.title)) {
        console.log(`Found "6th" in title: "${cls.title}"`);
        categories.add('Class 6th');
      }
      
      keywordPatterns.forEach(pattern => {
        if (pattern.regex.test(cls.title)) {
          if (pattern.category && pattern.category !== 'Class 6th') {
            // For predefined categories (except Class 6th which we handle separately)
            categories.add(pattern.category);
          } else if (pattern.regex.toString().includes('ssq')) {
            // For SSQ with dynamic numbers
            const match = cls.title.match(/ssq\s*(\d+)/i);
            if (match && match[1]) {
              categories.add(`SSQ ${match[1]}`);
            }
          }
        }
      });
    });
    
    // Force-add Class 6th for testing
    categories.add('Class 6th');
    
    // Sort categories with 'all' first, then alphabetically
    const sortedCategories = Array.from(categories).sort((a, b) => {
      if (a === 'all') return -1;
      if (b === 'all') return 1;
      return a.localeCompare(b);
    });
    
    console.log('Final extracted filters:', sortedCategories);
    setFilterCategories(sortedCategories);
  }, [RecordedCourse]);

  // Filter classes based on search term and active filter
  useEffect(() => {
    let result = RecordedCourse;
    
    // Apply search filter
    if (searchTerm) {
      result = result.filter(cls => 
        cls.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (cls.description && cls.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // Apply category filter
    if (activeFilter !== 'all') {
      const filterRegex = new RegExp(activeFilter, 'i');
      result = result.filter(cls => 
        filterRegex.test(cls.title)
      );
    }
    
    setFilteredClasses(result);
  }, [searchTerm, activeFilter, RecordedCourse]);

  // Search and Filter Component
  const SearchAndFilter = () => (
    <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
      <div className="relative w-full md:w-1/3">
        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search Recorded Coursees..."
          className="pl-10 pr-4 py-2.5 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
      
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex p-1 bg-gray-100 rounded-lg">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-md ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}
            title="Grid View"
          >
            <FiGrid className={viewMode === 'grid' ? 'text-blue-600' : 'text-gray-500'} />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-md ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
            title="List View"
          >
            <FiList className={viewMode === 'list' ? 'text-blue-600' : 'text-gray-500'} />
          </button>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          {filterCategories.map(category => (
            <button
              key={category}
              onClick={() => setActiveFilter(category)}
              className={`px-3 py-1.5 text-sm rounded-full transition-all ${
                activeFilter === category 
                  ? 'bg-blue-100 text-blue-700 font-medium'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {category === 'all' ? 'All' : category}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  // Format price display (enhanced)
  const formatPrice = (sellPrice, maxPrice) => {
    if (sellPrice <= 0) return { price: 'Free', discount: null, originalPrice: null };
    
    if (maxPrice > sellPrice) {
      const discountPercent = Math.round((1 - (sellPrice / maxPrice)) * 100);
      return {
        price: `₹${sellPrice}`,
        discount: `${discountPercent}%`,
        originalPrice: `₹${maxPrice}`
      };
    }
    
    return { price: `₹${sellPrice}`, discount: null, originalPrice: null };
  };

  // Class Card Component
  const ClassCard = ({ recordedCourse }) => {
    const { price, discount, originalPrice } = formatPrice(recordedCourse.sell_price, recordedCourse.max_price);
    const students = studentCounts[recordedCourse.content_id] || { total: 0, active: 0 };
    
    return (
      <div className="group bg-white border rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300">
        <div className="relative overflow-hidden">
          {recordedCourse.thumbnail ? (
            <div className="relative h-48 overflow-hidden">
              <img 
                src={recordedCourse.thumbnail} 
                alt={recordedCourse.title} 
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
            </div>
          ) : (
            <div className="w-full h-48 bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center group-hover:from-blue-600 group-hover:to-purple-700 transition-all duration-300">
              <h3 className="text-white text-xl font-bold px-4 text-center">{recordedCourse.title}</h3>
            </div>
          )}
          
          <div className="absolute top-3 right-3 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            {isAdmin && isPreviousCourse(recordedCourse) && (
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setCourseToDelete(recordedCourse);
                }}
                className="p-2 bg-white text-red-600 rounded-full shadow-md hover:bg-red-50 hover:shadow-lg transition-all duration-300"
                title="Delete Previous Course"
              >
                <FiTrash2 size={16} />
              </button>
            )}
            <button 
              onClick={(e) => {
                e.stopPropagation();
                setEditingId(recordedCourse.content_id);
                setShowForm(true);
              }}
              className="p-2 bg-white rounded-full shadow-md hover:shadow-lg transition-shadow duration-300"
            >
              <FiEdit style={{ color: currentColor }} />
            </button>
          </div>
          
          {discount && (
            <div className="absolute bottom-3 left-3 px-2.5 py-1.5 bg-red-500 text-white text-xs font-medium rounded-full">
              {discount} OFF
            </div>
          )}
        </div>
        
        <div className="p-5">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold group-hover:text-blue-600 transition-colors duration-300">
              {recordedCourse.title}
            </h3>
            {isPreviousCourse(recordedCourse) && (
              <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 rounded-full border border-amber-300">
                Previous
              </span>
            )}
          </div>
          
          <p className="text-gray-600 text-sm mb-4 line-clamp-2">
            {recordedCourse.description || 'No description available'}
          </p>
          
          <div className="flex flex-col gap-2 mb-4">
            <div className="flex items-center text-gray-600 text-sm">
              <FiClock className="mr-2 text-blue-500" />
              <span>
                {recordedCourse.access_end_date
                  ? `Access until ${new Date(recordedCourse.access_end_date).toLocaleDateString()}`
                  : 'Lifetime Access'}
              </span>
            </div>
            
            <div className="flex flex-col gap-1">
              <div className="flex items-center text-gray-600 text-sm">
                <FiUser className="mr-2 text-blue-500" />
                <span>Total Students: {students.total}</span>
              </div>
              <div className="flex items-center text-green-600 text-sm">
                <FiUser className="mr-2 text-green-500" />
                <span>Active Students: {students.active}</span>
              </div>
            </div>
          </div>
          
          <div className="pt-3 border-t flex justify-between items-center">
            <div className="flex flex-col">
              <span className="font-bold text-lg text-gray-800">{price}</span>
              {originalPrice && (
                <span className="text-gray-500 text-sm line-through">{originalPrice}</span>
              )}
            </div>
            
            <button
              onClick={() => navigate(`/recorded-content/${recordedCourse.content_id}`)}
              className="px-4 py-2 rounded-lg text-white text-sm font-medium transition-all duration-300 transform hover:scale-105"
              style={{ backgroundColor: currentColor }}
            >
              View Content
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Class List Item Component (for list view)
  const ClassListItem = ({ recordedCourse }) => {
    const { price, discount, originalPrice } = formatPrice(recordedCourse.sell_price, recordedCourse.max_price);
    const students = studentCounts[recordedCourse.content_id] || { total: 0, active: 0 };
    
    return (
      <div className="bg-white border rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 flex">
        <div className="relative w-40 h-auto">
          {recordedCourse.thumbnail ? (
            <img 
              src={recordedCourse.thumbnail} 
              alt={recordedCourse.title} 
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
              <h3 className="text-white text-lg font-bold px-2 text-center">{recordedCourse.title.slice(0, 20)}</h3>
            </div>
          )}
          
          {discount && (
            <div className="absolute bottom-2 left-2 px-2 py-1 bg-red-500 text-white text-xs font-medium rounded-full">
              {discount} OFF
            </div>
          )}
        </div>
        
        <div className="flex-1 p-4">
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold mb-2">{recordedCourse.title}</h3>
              {isPreviousCourse(recordedCourse) && (
                <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 rounded-full border border-amber-300 mb-2">
                  Previous
                </span>
              )}
            </div>
            
            <div className="flex items-center gap-1">
              {isAdmin && isPreviousCourse(recordedCourse) && (
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    setCourseToDelete(recordedCourse);
                  }}
                  className="p-1.5 text-red-600 hover:bg-red-50 rounded-full transition-colors duration-300"
                  title="Delete Previous Course"
                >
                  <FiTrash2 size={16} />
                </button>
              )}
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingId(recordedCourse.content_id);
                  setShowForm(true);
                }}
                className="p-1.5 hover:bg-gray-100 rounded-full transition-colors duration-300"
                title="Edit Course"
              >
                <FiEdit style={{ color: currentColor }} />
              </button>
            </div>
          </div>
          
          <p className="text-gray-600 text-sm mb-3 line-clamp-1">
            {recordedCourse.description || 'No description available'}
          </p>
          
          <div className="flex flex-wrap gap-4 mb-3">
            <div className="flex items-center text-gray-600 text-sm">
              <FiClock className="mr-1.5 text-blue-500" />
              <span>
                {recordedCourse.access_end_date
                  ? `Until ${new Date(recordedCourse.access_end_date).toLocaleDateString()}`
                  : 'Lifetime'}
              </span>
            </div>
            
            <div className="flex flex-col gap-1">
              <div className="flex items-center text-gray-600 text-sm">
                <FiUser className="mr-1.5 text-blue-500" />
                <span>Total: {students.total}</span>
              </div>
              <div className="flex items-center text-green-600 text-sm">
                <FiUser className="mr-1.5 text-green-500" />
                <span>Active: {students.active}</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="w-48 p-4 border-l flex flex-col justify-center items-center">
          <div className="mb-3 text-center">
            <div className="font-bold text-lg text-gray-800">{price}</div>
            {originalPrice && (
              <div className="text-gray-500 text-sm line-through">{originalPrice}</div>
            )}
          </div>
          
          <button
              onClick={() => navigate(`/recorded-content/${recordedCourse.content_id}`)}
              className="px-4 py-2 rounded-lg text-white text-sm font-medium transition-all duration-300 transform hover:scale-105"
              style={{ backgroundColor: currentColor }}
            >
              View Content
            </button>
        </div>
      </div>
    );
  };

  // Form Component - Using the existing one with styling improvements
  const RecordedCourseForm = () => {
    // Using state for form inputs
    const [formState, setFormState] = useState({
      title: '',
      description: '',
      sell_price: 0,
      max_price: 0,
      access_end_date: '',
      thumbnail: null,
      thumbnailPreview: ''
    });

    // Load data if editing
    useEffect(() => {
      if (editingId) {
        const loadClassData = async () => {
          try {
            const response = await axios.get(`https://apissc.sainikschoolcadet.com/api/Contents/${editingId}`);
            const classData = response.data;
            
            // Format the date for input if it exists
            let formattedDate = '';
            if (classData.access_end_date) {
              // Format as YYYY-MM-DD for date input
              const date = new Date(classData.access_end_date);
              formattedDate = date.toISOString().split('T')[0];
            }
            
            setFormState({
              title: classData.title || '',
              description: classData.description || '',
              sell_price: classData.sell_price || 0,
              max_price: classData.max_price || 0,
              access_end_date: formattedDate,
              thumbnail: null,
              thumbnailPreview: classData.thumbnail || ''
            });
          } catch (err) {
            console.error('Error loading class data:', err);
            setFormError('Failed to load class data for editing');
          }
        };
        
        loadClassData();
      }
    }, []);

    const handleInputChange = (e) => {
      const { name, value, type, checked } = e.target;
      setFormState(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    };

    const handleThumbnailChange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFormState(prev => ({
            ...prev,
            thumbnail: file,
            thumbnailPreview: reader.result
          }));
        };
        reader.readAsDataURL(file);
      }
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      setSubmitting(true);
      setFormError(null);
      
      try {
        const formData = new FormData();
        
        // Add all form fields
        formData.append('title', formState.title);
        formData.append('description', formState.description);
        formData.append('content_type', 'recorded_lecture');
        formData.append('sell_price', formState.sell_price);
        formData.append('max_price', formState.max_price);
        if (formState.access_end_date) {
          formData.append('access_end_date', formState.access_end_date);
        }
        
        if (formState.thumbnail) {
          formData.append('thumbnail', formState.thumbnail);
        }
        
        if (editingId) {
          await axios.put(
            `https://apissc.sainikschoolcadet.com/api/Contents/${editingId}`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );
        } else {
          await axios.post(
            'https://apissc.sainikschoolcadet.com/api/Contents/',
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );
        }
        
        // Refresh the Recorded Coursees
        const response = await axios.get('https://apissc.sainikschoolcadet.com/api/Contents/type/recorded_lecture');
        const classes = response.data.data;
        setRecordedCourse(classes);
        setFilteredClasses(classes);
        
        // Refresh student counts if necessary
        if (!editingId) { // Only fetch counts for new class
          try {
            const studentsResponse = await axios.get(`https://apissc.sainikschoolcadet.com/api/StudentContentAccess/content/${classes[classes.length - 1].content_id}/students`);
            setStudentCounts(prev => ({
              ...prev,
              [classes[classes.length - 1].content_id]: {
                total: studentsResponse.data.total_students_count,
                active: studentsResponse.data.active_students_count
              }
            }));
          } catch (error) {
            console.error(`Error fetching students for new class:`, error);
          }
        }
        
        // Close the form
        setShowForm(false);
        setEditingId(null);
      } catch (err) {
        console.error('Error saving Recorded Course:', err);
        setFormError(err.response?.data?.message || 'Failed to save Recorded Course. Please try again.');
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto backdrop-blur-sm">
        <div className="bg-white rounded-xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">
              {editingId ? "Edit Recorded Course" : "Create Recorded Course"}
            </h2>
            <button 
              type="button" 
              onClick={() => {
                setShowForm(false);
                setEditingId(null);
              }} 
              className="p-2 rounded-full hover:bg-gray-100 transition-colors duration-300"
            >
              <FiX />
            </button>
          </div>
          
          {formError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-600 rounded-md">
              {formError}
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-6">
              {/* Title */}
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formState.title}
                  onChange={handleInputChange}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Class 9th Preparation"
                />
              </div>
              
              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formState.description}
                  onChange={handleInputChange}
                  rows="4"
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Describe what this Recorded Course includes"
                />
              </div>
              
              {/* Price Section */}
              <div className="bg-gray-50 p-5 rounded-lg">
                <h3 className="font-medium mb-3 flex items-center">
                  <FiDollarSign className="mr-2 text-blue-500" />
                  Pricing Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="sell_price" className="block text-sm font-medium text-gray-700 mb-1">
                      Selling Price (₹) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      id="sell_price"
                      name="sell_price"
                      value={formState.sell_price}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      required
                      className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Current selling price</p>
                  </div>
                  <div>
                    <label htmlFor="max_price" className="block text-sm font-medium text-gray-700 mb-1">
                      Maximum Price (₹)
                    </label>
                    <input
                      type="number"
                      id="max_price"
                      name="max_price"
                      value={formState.max_price}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Original/regular price (for discount display)</p>
                  </div>
                </div>
              </div>
              
              {/* Access End Date */}
              <div>
                <label htmlFor="access_end_date" className="block text-sm font-medium text-gray-700 mb-1">
                  Access End Date
                </label>
                <input
                  type="date"
                  id="access_end_date"
                  name="access_end_date"
                  value={formState.access_end_date}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leave empty for lifetime access
                </p>
              </div>
              
              {/* Thumbnail */}
              <div>
                <label htmlFor="thumbnail" className="block text-sm font-medium text-gray-700 mb-1">
                  Thumbnail Image
                </label>
                
                {formState.thumbnailPreview && (
                  <div className="mb-3">
                    <img 
                      src={formState.thumbnailPreview} 
                      alt="Thumbnail preview" 
                      className="w-40 h-40 object-cover border rounded-md"
                    />
                  </div>
                )}
                
                <input
                  type="file"
                  id="thumbnail"
                  name="thumbnail"
                  onChange={handleThumbnailChange}
                  accept="image/*"
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Recommended size: 800x450px (16:9 ratio)
                </p>
              </div>
              
              {/* Submit Button */}
              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingId(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm mr-3 hover:bg-gray-50 transition-colors duration-300"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white rounded-md shadow-sm transition-all duration-300 hover:shadow-md"
                  style={{ backgroundColor: currentColor }}
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : (editingId ? 'Update Recorded Course' : 'Create Recorded Course')}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // If loading
  if (loading && !showForm) {
    return (
      <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
        <Header category="Content" title="Recorded Coursees" />
        <div className="flex justify-center items-center h-64">
          <div className="animate-pulse flex flex-col items-center">
            <div className="w-12 h-12 rounded-full bg-blue-200 mb-3"></div>
            <p className="text-xl text-gray-600">Loading Recorded Coursees...</p>
          </div>
        </div>
      </div>
    );
  }

  // Main Recorded Course listing
  return (
    <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl shadow-sm">
      <div className="flex justify-between items-center mb-6">
        <Header category="Content" title="Recorded Coursees" />
        <button 
          onClick={() => {
            setEditingId(null);
            setShowForm(true);
          }}
          className="px-5 py-2.5 rounded-lg text-white text-sm font-medium flex items-center gap-2 shadow-sm transition-all duration-300 hover:shadow-md transform hover:scale-105"
          style={{ backgroundColor: currentColor }}
        >
          <FiPlus size={18} /> Add New Recorded Course
        </button>
      </div>

      {error && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 text-red-600 rounded-md">
          {error}
        </div>
      )}

      {/* Search and Filter */}
      <SearchAndFilter />

      <div className="mt-6">
        {filteredClasses.length === 0 ? (
          <div className="bg-gray-50 rounded-lg p-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center">
                <FiPlus className="text-gray-500 text-2xl" />
              </div>
            </div>
            <p className="text-gray-600 mb-4">
              {RecordedCourse.length > 0 
                ? 'No classes match your search criteria.' 
                : 'No Recorded Coursees available yet.'}
            </p>
            <button
              onClick={() => {
                setEditingId(null);
                setShowForm(true);
              }}
              className="px-4 py-2 rounded-md text-white text-sm inline-flex items-center gap-2 transition-all duration-300 hover:shadow-md"
              style={{ backgroundColor: currentColor }}
            >
              <FiPlus /> Create Recorded Course
            </button>
          </div>
        ) : ( 
          <> 
            {viewMode === 'grid' ? ( 
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"> 
                {filteredClasses.map((recordedCourse) => ( 
                  <ClassCard key={recordedCourse.content_id} recordedCourse={recordedCourse} /> 
                ))} 
              </div> 
            ) : ( 
              <div className="flex flex-col gap-4"> 
                {filteredClasses.map((recordedCourse) => ( 
                  <ClassListItem key={recordedCourse.content_id} recordedCourse={recordedCourse} /> 
                ))} 
              </div> 
            )} 
          </> 
        )}
      </div>

      {showForm && <RecordedCourseForm />}

      {/* Notification Toast */}
      {notification && (
        <div className="fixed bottom-6 right-6 z-50">
          <div className={`px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 text-sm font-medium ${
            notification.type === 'success' 
              ? 'bg-green-600 text-white' 
              : 'bg-red-600 text-white'
          }`}>
            <span>{notification.message}</span>
          </div>
        </div>
      )}

      {/* Delete Course Confirmation Dialog */}
      {courseToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-6">
              <div className="w-12 h-12 rounded-full bg-red-100 text-red-500 flex items-center justify-center mx-auto mb-4">
                <FiTrash2 className="text-xl" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Course?</h3>
              <p className="text-sm text-gray-500">
                Are you sure you want to delete this previous course?
              </p>
              <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg text-left">
                <p className="font-semibold text-sm text-gray-800">{courseToDelete.title}</p>
                {courseToDelete.access_end_date && (
                  <p className="text-xs text-gray-500 mt-1">
                    Expired on: {new Date(courseToDelete.access_end_date).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setCourseToDelete(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-300"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteCourse}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors duration-300 flex items-center gap-1.5"
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecordedCourse;